import React, { useState } from 'react';
import { Unlock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * Superadmin-only tool: instantly clear a locked-out account's login
 * rate-limit (5 failed attempts / 15 min) instead of waiting out the window.
 * Works for any account email - a tenant admin, or another superadmin.
 *
 * Props:
 *   unlockAccount: (email: string) => Promise<{ data: { cleared_attempts, email } }>
 */
export default function UnlockAccountPanel({ unlockAccount }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const handleUnlock = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error('Enter the account email to unlock');
      return;
    }
    setBusy(true);
    try {
      const response = await unlockAccount(trimmed);
      const cleared = response.data.cleared_attempts;
      if (cleared > 0) {
        toast.success(`Unlocked ${response.data.email} - ${cleared} failed attempt${cleared === 1 ? '' : 's'} cleared`);
      } else {
        toast.success(`${response.data.email} had no active lockout - nothing to clear`);
      }
      setEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock account');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="unlock-account-panel">
      <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
        Unlock a Locked-Out Account
      </h2>
      <p className="text-gray-500 mb-6 text-sm max-w-2xl">
        After 5 failed login attempts, an account is locked out for 15 minutes as a
        brute-force protection. Enter the account's email below to clear that lockout
        instantly - works for any tenant admin or superadmin account. Note: if this is
        the ONLY superadmin account and it's the one locked out, you won't be able to
        reach this page until the 15 minutes pass, since it requires being signed in.
      </p>

      <form onSubmit={handleUnlock} className="bg-gray-50 rounded-lg border border-[#F3F4F6] p-6 max-w-lg space-y-3">
        <Label htmlFor="unlock-email">Account Email</Label>
        <Input
          id="unlock-email"
          type="email"
          placeholder="admin@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="bg-white border-[#F3F4F6]"
          data-testid="unlock-account-email-input"
        />
        <Button
          type="submit"
          disabled={busy}
          className="bg-[#4A6741] hover:bg-[#3d5636] flex items-center gap-2"
          data-testid="unlock-account-submit-btn"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlock className="h-4 w-4" />}
          {busy ? 'Unlocking...' : 'Unlock Account'}
        </Button>
      </form>
    </div>
  );
}
